import { useRequest } from 'redux-query-react';
import { UseDataInfo } from 'QueryTypes';
import { useAppSelector } from 'utils/hooks';
import { AppointmentType } from 'EntityTypes';
import { useCurrentPractice } from 'modules/practice';
import { factoryUseReferencesInWorkspace, ReferenceEdgeType } from 'modules/workspaces';
import { appointmentTypesQuery } from './appointmentTypesQueries';
import { getAppointmentTypes, getAppointmentTypesUnfiltered } from './appointmentTypesSelectors';

/**
 * Fetches and selects appointment types for the given `practiceID`.
 *
 * @param practiceId - The ID of the practice
 * @param ignoreWorkspace - When true, bypasses server-side workspace filtering to return all
 *                          appointment types across the practice. Use on any screen that manages
 *                          practice-wide configuration (e.g., settings, availability, forms)
 *                          rather than presenting appointment choices within a specific workspace.
 *                          Results are stored in a separate Redux key (`byIdUnfiltered`) so
 *                          they cannot be overwritten by workspace-filtered scheduler queries.
 *                          When false (default), returns only types in the current workspace;
 *                          use in the scheduler and appointment-creation dialogs.
 */
export const useAppointmentTypes = (
  practiceId: number,
  ignoreWorkspace = false,
): UseDataInfo<AppointmentType[] | undefined> => {
  const [queryState, refresh] = useRequest(appointmentTypesQuery(practiceId, ignoreWorkspace));
  const appointmentTypes = useAppSelector(
    ignoreWorkspace ? getAppointmentTypesUnfiltered : getAppointmentTypes,
  );
  return [appointmentTypes, queryState, refresh];
};

/**
 * Fetches ALL appointment types for the current practice, bypassing workspace filtering.
 * Used for workspace configuration where all appointment types should be visible.
 */
export const useAllAppointmentTypes = (): UseDataInfo<AppointmentType[] | undefined> => {
  const practice = useCurrentPractice();
  return useAppointmentTypes(practice.id, true);
};

/**
 * Fetches and Selects the Appointment Types that have References to a specific
 * Workspace.
 */
export const useAppointmentTypeReferences = factoryUseReferencesInWorkspace<AppointmentType>(
  'AppointmentType',
  useAllAppointmentTypes,
  ReferenceEdgeType.PracticeWorkspaceFilter,
);
